// frontend/src/components/RouteList.js

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-toastify';
import { getRoutes } from '../services/routeService';
import { getGlobalThreshold } from '../utils/preferences';

const RouteList = ({ currentEntity, setselectRoute, selectedRoute }) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const [routes, setRoutes] = useState([]);  
    const [searchTerm, setSearchTerm] = useState('');
    const globalThreshold = getGlobalThreshold();

    useEffect(() => {
        // console.log('Fetching shuttle paths for entityId:', currentEntity.entityId);  // Log to check entityId
        if (!currentEntity?.entityId) {
            return undefined;
        }

        // Ignore late responses when the active entity changes before the route
        // request settles, so a stale entity cannot repopulate the visible list.
        let isCurrentRequest = true;

        getRoutes(currentEntity.entityId, { includeInactive: false, include: 'stops' })
            .then((routeData) => {
                if (isCurrentRequest) {
                    setRoutes(routeData);
                }
            })
            .catch((error) => {
                if (isCurrentRequest) {
                    console.error('Error fetching routes:', error);
                    toast.error(t('errorFetchingRoutes'));
                }
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [currentEntity?.entityId, t]);  // Re-fetch routes when currentEntity changes

    // Filter routes based on search term
    const filteredRoutes = useMemo(() => {
        if (searchTerm.trim() === '') {
            return routes;
        }
     
        const searchTermLower = searchTerm.toLowerCase();
        return routes.filter(route => {
            const stops = route.waypoints?.sort((a, b) => a.sequence_number - b.sequence_number);
            if (!stops?.length) return false;
     
            return route.name.toLowerCase().includes(searchTermLower) ||
                stops.some(stop => 
                    stop.name.toLowerCase().includes(searchTermLower) ||
                    stop.address.toLowerCase().includes(searchTermLower)
                );
        });
    }, [searchTerm, routes]);

    return (
        <div className={cn(
            "w-full lg:w-1/3 p-6 flex flex-col h-[300px] lg:h-full border-r",
            background.primary,
            border.default
        )}>
            {/* Route List Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                <h2 className={cn("text-2xl font-bold", text.primary)}>{t('Routes')} ({routes.length})</h2>
                {routes.length >= globalThreshold && (
                    <input
                        type="text"
                        className={cn(
                            "px-3 py-2 rounded-lg text-sm transition-colors",
                            "border-2 border-gray-300 dark:border-gray-600",
                            "focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800",
                            "bg-white dark:bg-gray-700",
                            text.primary,
                            "placeholder-gray-500 dark:placeholder-gray-400"
                        )}
                        placeholder={t('searchRoutes')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                )}
            </div>
            {/* Route List */}
            <div className="flex-grow overflow-y-auto">
                <ul className="space-y-2">
                    {filteredRoutes.map(route => {
                        const orderedStops = route.waypoints?.filter(point => point.point_type === 'stop')?.sort((a, b) => a.sequence_number - b.sequence_number) || [];
                        const isSelected = selectedRoute && selectedRoute.id === route.id;
                        
                        return (
                            <li 
                                key={route.id}
                                className={cn(
                                    "cursor-pointer p-4 rounded-lg transition-all duration-200 border",
                                    isSelected 
                                        ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700" 
                                        : cn(
                                            "border-gray-200 dark:border-gray-700",
                                            "hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600"
                                          )
                                )}
                                onClick={() => setselectRoute(route)}
                            >
                                <div className={cn(
                                    "font-semibold",
                                    isSelected ? "text-blue-700 dark:text-blue-400" : text.primary
                                )}>{route.name}</div>
                                {isSelected && (
                                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                        {orderedStops.length > 0 ? (
                                            <ul className="space-y-2">
                                                {orderedStops.map((stop, index, array) => {
                                                    const isStart = index === 0;
                                                    const isEnd = index === array.length - 1;
                                                    
                                                    return (
                                                        <li key={index} className={cn(
                                                            "text-sm p-2 rounded bg-gray-50 dark:bg-gray-700/50",
                                                            text.secondary
                                                        )}>
                                                            <span className="font-medium">
                                                                {isStart ? `🚏 ${t('start')}: ` :
                                                                isEnd ? `🏁 ${t('end')}: ` :
                                                                `🔸${stop.name}: `}
                                                            </span>
                                                            <span className={text.muted}>{stop.address}</span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <p className={cn("italic text-sm", text.muted)}>{t('noStops')}</p>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
            
        </div>
    );
};

export default RouteList;
